import {useCallback, useEffect} from "react";
import {Asset, MarketWidgetIdentifier} from "../api/api";
import {dispatcher} from "../store";
import {useStoreSelector} from "./redux/useStoreSelector";
import {useFetching} from "./system/useFetching";
import {useNotify} from "./system/useNotify";
import {useApi} from "./useApi";
import {useTradableAssets} from "./useTradableAssets";

export function useMarketWidgets() {
  const marketWidgets = useStoreSelector( s => s.marketWidgets.list ),
    { tradableAssets } = useTradableAssets(),
    { withFetching } = useFetching(false),
    { api, isAuth } = useApi(),
    { addNotify, handleApiError } = useNotify();

  const loadMarketWidgets = useCallback(async () => {
    if (isAuth && tradableAssets && !marketWidgets) {
      const res = await withFetching(api.getMarketWidgets({})
        .then(handleApiError)
        .catch(e => addNotify('error', 'Error', e.message)));

      if (res) {
        let assetIdMap: {[assetId: number]: Asset} = {};
        if (tradableAssets) {
          tradableAssets.forEach(( asset: Asset ) => {
            assetIdMap[asset.id] = asset;
          });
        }
        
        let instrumentTickers: MarketWidgetIdentifier[] = res.map(instrument => {
            const quoteCurrencyId   = instrument.quoteCurrencyId!
            const assetTicker       = (assetIdMap[instrument.assetId]) ? assetIdMap[instrument.assetId].ticker : "",
            quoteCurrencyTicker     = (assetIdMap[quoteCurrencyId]) ? assetIdMap[quoteCurrencyId].ticker : ""
            const instrumentTicker  = ((instrument.ticker !== undefined && instrument.ticker !== "") ?
                                          instrument.ticker! :
                                          (instrument.contraAssetId !== undefined && instrument.contraAssetId !== 0) ?
                                              (assetIdMap[instrument.assetId]?.ticker + '/' + assetIdMap[instrument.contraAssetId!]?.ticker) :
                                              assetIdMap[instrument.assetId]?.ticker)
            const quantityTicker    = (instrument.qtyInQuoteAsset !== undefined && instrument.qtyInQuoteAsset) ? quoteCurrencyTicker : instrumentTicker

            return {
              'instrumentID'        : instrument.instrumentId,
              'instrumentTicker'    : instrumentTicker,
              'quoteCurrencyTicker' : quoteCurrencyTicker,
              'quantityAssetTicker' : quantityTicker,
              'baseAssetTicker'     : assetTicker
            }

        })
        instrumentTickers.push({instrumentID: 0, quoteCurrencyTicker: "", baseAssetTicker: "", quantityAssetTicker: "", instrumentTicker: "ADD_MARKET_WIDGET"});

        // Update view
        dispatcher.marketWidgets.setList(instrumentTickers);
      }
    }
  }, [marketWidgets, addNotify, api, handleApiError, isAuth, tradableAssets, withFetching])


  const setMarketWidgets = useCallback((newMarketWidgets: MarketWidgetIdentifier[]) => {
    if (isAuth) {
      // Update view
      dispatcher.marketWidgets.setList(newMarketWidgets)

      // Save user's market widget layout
      const newMarketWidgetsForUser: number[] = newMarketWidgets.filter(( marketWidget: MarketWidgetIdentifier ) => {
        return marketWidget.instrumentID !== 0
      }).map(( marketWidget: MarketWidgetIdentifier ) => {
        return marketWidget.instrumentID
      });
      api.putMarketWidgets({'marketWidgets': newMarketWidgetsForUser})
        .then(handleApiError)
        .catch(e => addNotify('error', 'Error', e.message))
    }
  }, [addNotify, api, handleApiError, isAuth])

  useEffect(() => {
    if (!marketWidgets && isAuth) {
      loadMarketWidgets();
    }
  }, [marketWidgets, isAuth, loadMarketWidgets])

  return {marketWidgets, loadMarketWidgets, setMarketWidgets}
}